import { useMemo } from "react";
import { useSelector } from "react-redux";

import HasSourceObject from "@mapmycustomers/shared/types/base/HasSourceObject";
import {
  EntityType,
  EntityTypesSupportingFieldCustomization,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FormLayout, {
  ChildLayout,
  LayoutSchemaField,
} from "@mapmycustomers/shared/types/layout/FormLayout";
import {
  isDefined,
  isEntityTypeSupportingCustomSourceObject,
} from "@mapmycustomers/shared/util/assert";

import { getFunnelStages } from "@app/store/deal";
import getChildLayout from "@app/util/layout/getChildLayout";

interface UseActualLayoutSchemaArgs {
  crmActivityTypeId?: ActivityType["id"];
  entityType: EntityTypesSupportingFieldCustomization;
  layout: FormLayout;
  sourceObject?: HasSourceObject["sourceObject"];
  stageId?: Stage["id"];
}

const useActualLayoutSchema = ({
  crmActivityTypeId,
  entityType,
  layout,
  sourceObject,
  stageId,
}: UseActualLayoutSchemaArgs): [
  actualSchema: LayoutSchemaField[],
  childLayout: ChildLayout | undefined,
  isVariant: boolean,
] => {
  const funnelStages = useSelector(getFunnelStages);

  return useMemo(() => {
    const funnelIdString = isDefined(stageId)
      ? Object.keys(funnelStages).find((funnelId) =>
          funnelStages[parseInt(funnelId) as Funnel["id"]].some(({ id }) => id === stageId)
        )
      : undefined;

    const childLayout = getChildLayout(
      layout,
      entityType === EntityType.DEAL && isDefined(funnelIdString) && isDefined(stageId)
        ? { funnelId: parseInt(funnelIdString), stageId }
        : entityType === EntityType.ACTIVITY && isDefined(crmActivityTypeId)
          ? { activityTypeId: crmActivityTypeId }
          : isEntityTypeSupportingCustomSourceObject(entityType) && isDefined(sourceObject)
            ? { sourceObject }
            : undefined
    );

    const schema = childLayout?.schema ?? layout.schema;
    const isVariant = !!childLayout;

    return [schema, childLayout, isVariant];
  }, [crmActivityTypeId, entityType, funnelStages, layout, sourceObject, stageId]);
};

export default useActualLayoutSchema;
